import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

const PullToRefreshContext = createContext(null);

export { PullToRefreshContext };

/**
 * App-wide pull-to-refresh registry (native only). The active route may register
 * a refresh handler; if none is registered, refresh falls back to a full reload.
 */
export function PullToRefreshProvider({ children }) {
  const handlerRef = useRef(null);

  const setRefreshHandler = useCallback((handler) => {
    handlerRef.current = handler;
  }, []);

  const runRefresh = useCallback(async () => {
    const handler = handlerRef.current;
    if (handler) {
      await handler();
      return;
    }
    window.location.reload();
  }, []);

  const value = useMemo(
    () => ({ setRefreshHandler, runRefresh }),
    [setRefreshHandler, runRefresh]
  );

  return (
    <PullToRefreshContext.Provider value={value}>
      {children}
    </PullToRefreshContext.Provider>
  );
}

export function usePullToRefreshContext() {
  const ctx = useContext(PullToRefreshContext);
  if (!ctx) {
    throw new Error('usePullToRefreshContext must be used within PullToRefreshProvider');
  }
  return ctx;
}
